<script setup lang="ts">
import { ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 变更零件的库存清点表（0826 Tony 给的纸质表：成品/零件 × 数量 × 位置）。
 *
 * 一张变更一张表、六个位置固定成六行。这里只渲染**这一条措施该管的那几行**：
 *   · EX03 只看「供应商」一行，EX12 看其余五行 —— 谁填哪一行有主；
 *   · ST01–ST06 各看自己那一行，且是只读的 —— 处置的人要的是"数出来多少"，
 *     不是再数一遍。同一个数只在一处填，处置时看到的就是清点的结果。
 *
 * 数量空着 = 还没数，填 0 = 数过了是零。这两件事在断点追溯时结论完全不同，
 * 所以空值不给默认 0，界面上也分开显示（「—」和「0」）。
 */
interface InventoryRow {
  id: number;
  location: string;
  itemName: string | null;
  quantity: number | null;
  /** 处置完还剩多少（0827 Tony 的表）——和数量分开，别把「原来有多少」冲掉 */
  remainingQuantity: number | null;
  /** 这一行的旧件怎么处置 */
  disposition: string | null;
  countedByName: string | null;
  countedAt: string | null;
}

type Field = 'itemName' | 'quantity' | 'remainingQuantity' | 'disposition';

/**
 * 哪一格归谁填（0827 Tony 的表）。
 *
 * 数量归**数的人**（EX03/EX12），剩余数量和处理方式归**处置的人**（ST01–ST06）——
 * 这正是这张表拆成两拨人的原因：数的人只管数出来多少，
 * 处置的人拿着这个数去处置，再回填处置方式和剩下多少。
 * 让一个人从头填到尾，「原来有多少」和「处置完还剩多少」就会被写成同一个数。
 */
const FIELD_MODE: Readonly<Record<Field, 'count' | 'dispose'>> = {
  itemName: 'count', quantity: 'count',
  remainingQuantity: 'dispose', disposition: 'dispose',
};

/** 处理方式的选项；和后端 INVENTORY_DISPOSITIONS 同一份，改要一起改 */
const DISPOSITIONS = ['用完为止', '返工使用', '报废', '退供应商', '客户处追回'];

const props = defineProps<{
  changeId: string | number;
  rows: InventoryRow[];
  /** count = 这条措施负责数，可以填；dispose = 处置方，只看数 */
  mode: 'count' | 'dispose';
  /** 还没定责任人时不展开表格，只留一句话——定了责任人才是"该谁去数"有主的时候 */
  ownerName?: string | null;
  editable: boolean;
}>();

const emit = defineEmits<{ saved: [] }>();

const editing = ref<{ id: number; field: Field } | null>(null);
const editValue = ref('');
const saving = ref(false);

function focusCell(el: unknown): void {
  if (el instanceof HTMLInputElement) el.focus();
}
function isEditing(row: InventoryRow, field: Field): boolean {
  return editing.value?.id === row.id && editing.value.field === field;
}
/** 这一格在这条措施下点不点得动 */
function canEdit(field: Field): boolean {
  return props.editable && FIELD_MODE[field] === props.mode;
}
function beginEdit(row: InventoryRow, field: Field): void {
  if (!canEdit(field)) return;
  editing.value = { id: row.id, field };
  editValue.value = row[field] === null || row[field] === undefined ? '' : String(row[field]);
}
function cancelEdit(): void {
  editing.value = null;
  editValue.value = '';
}
async function commitEdit(row: InventoryRow, field: Field, next?: string): Promise<void> {
  const value = (next ?? editValue.value).trim();
  const previous = row[field] === null || row[field] === undefined ? '' : String(row[field]);
  cancelEdit();
  if (value === previous) return;
  const numeric = field === 'quantity' || field === 'remainingQuantity';
  if (numeric && value !== '' && !Number.isFinite(Number(value))) {
    notify('error', '要填数字', `「${value}」不是数字。数不出来就先空着。`);
    return;
  }
  saving.value = true;
  try {
    await eqsApi.updateChangeInventory(props.changeId, row.id, { [field]: value === '' ? null : value });
    emit('saved');
  } catch (caught) {
    notify('error', '这一格没存上', errorMessage(caught));
    emit('saved');
  } finally {
    saving.value = false;
  }
}

/**
 * 进度：数的人看「数完几行」，处置的人看「处置完几行」——
 * 两拨人干的不是同一件事，摆同一个进度会让处置的人以为自己没活了。
 */
function countedText(): string {
  if (props.mode === 'dispose') {
    const done = props.rows.filter((row) => row.disposition).length;
    return `${done}/${props.rows.length} 已定处置`;
  }
  const done = props.rows.filter((row) => row.quantity !== null).length;
  return `${done}/${props.rows.length} 已清点`;
}
</script>

<template>
  <p v-if="mode === 'count' && !ownerName" class="inv-wait">
    定了责任人之后，这里会展开这条措施要数的{{ rows.length }}个位置的库存清点表。
  </p>
  <div v-else class="inv-wrap">
    <p class="inv-head">
      <b>库存清点表</b>
      <span v-if="mode === 'count'">这几行归你数<em v-if="ownerName">（{{ ownerName }}）</em></span>
      <span v-else>这几行归你处置<em v-if="ownerName">（{{ ownerName }}）</em>：数量只看，填右边两格</span>
      <em class="inv-progress">{{ saving ? '保存中…' : countedText() }}</em>
    </p>
    <table>
      <thead>
        <!-- 列序照 0827 Tony 的手绘表：位置右边是剩余数量和处理方式 -->
        <tr>
          <th>成品 / 零件</th><th>数量</th><th>位置 Location</th>
          <th>剩余数量</th><th>处理方式</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <td
            class="inv-name" :class="{ 'inv-edit': mode === 'count' && editable, 'is-editing': isEditing(row, 'itemName') }"
            @click="beginEdit(row, 'itemName')"
          >
            <input
              v-if="isEditing(row, 'itemName')"
              :ref="focusCell" v-model="editValue" type="text" placeholder="成品 / 零件"
              @keydown.enter="commitEdit(row, 'itemName')"
              @blur="commitEdit(row, 'itemName')"
              @keydown.esc="cancelEdit"
            >
            <template v-else>{{ row.itemName || '—' }}</template>
          </td>
          <td
            class="inv-qty" :class="{ 'inv-edit': mode === 'count' && editable, 'is-editing': isEditing(row, 'quantity'), 'is-blank': row.quantity === null }"
            :title="row.countedByName ? `${row.countedByName} 清点` : '还没数'"
            @click="beginEdit(row, 'quantity')"
          >
            <input
              v-if="isEditing(row, 'quantity')"
              :ref="focusCell" v-model="editValue" type="text" placeholder="数量"
              @keydown.enter="commitEdit(row, 'quantity')"
              @blur="commitEdit(row, 'quantity')"
              @keydown.esc="cancelEdit"
            >
            <template v-else>{{ row.quantity === null ? '—' : row.quantity }}</template>
          </td>
          <td class="inv-loc">{{ row.location }}</td>
          <!--
            剩余数量和处理方式归**处置的人**（ST01–ST06）填，数的人只读：
            数的人报「原来有多少」，处置的人回填「怎么处置的、还剩多少」。
          -->
          <td
            class="inv-remain"
            :class="{ 'inv-edit': canEdit('remainingQuantity'), 'is-editing': isEditing(row, 'remainingQuantity'), 'is-blank': row.remainingQuantity === null }"
            @click="beginEdit(row, 'remainingQuantity')"
          >
            <input
              v-if="isEditing(row, 'remainingQuantity')"
              :ref="focusCell" v-model="editValue" type="text" placeholder="剩余"
              @keydown.enter="commitEdit(row, 'remainingQuantity')"
              @blur="commitEdit(row, 'remainingQuantity')"
              @keydown.esc="cancelEdit"
            >
            <template v-else>{{ row.remainingQuantity === null ? '—' : row.remainingQuantity }}</template>
          </td>
          <td class="inv-disp" :class="{ 'is-blank': !row.disposition }">
            <select
              v-if="canEdit('disposition')"
              :value="row.disposition ?? ''"
              @change="commitEdit(row, 'disposition', ($event.target as HTMLSelectElement).value)"
            >
              <option value="">未定</option>
              <option v-for="option in DISPOSITIONS" :key="option" :value="option">{{ option }}</option>
            </select>
            <template v-else>{{ row.disposition || '—' }}</template>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="mode === 'count'" class="inv-note">
      点格子直接填。<b>数量空着=还没数，填 0=数过了是零</b>，这两件事在断点追溯时结论不一样。
      六行数量填满之后这条措施就能标完成，不用再另外传一份 Excel。
      <br>剩余数量和处理方式归处置那条措施填，这里只看。
    </p>
    <p v-else class="inv-note">
      数量是清点的人填的，这里只看。<b>你要填的是右边两格</b>：怎么处置、处置完还剩多少。
    </p>
  </div>
</template>

<style scoped>
.inv-wait { margin: 6px 0 0; padding: 6px 10px; border-left: 3px solid var(--line); color: var(--muted); font-size: 12px; }
.inv-wrap { margin: 6px 0 0; padding: 8px 10px; border-left: 3px solid var(--blue); background: #F5F8FC; }
.inv-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px; margin: 0 0 6px; font-size: 12px; }
.inv-head b { font-size: 12px; }
.inv-head span { color: var(--muted); }
.inv-head em { font-style: normal; }
.inv-progress { margin-left: auto; color: var(--muted); }
.inv-wrap table { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; }
.inv-wrap th, .inv-wrap td { padding: 5px 8px; border: 1px solid var(--line); text-align: left; }
.inv-wrap th { color: var(--muted); background: #f3f5f8; white-space: nowrap; }
.inv-loc { white-space: nowrap; color: var(--muted); }
.inv-qty { width: 90px; font-variant-numeric: tabular-nums; }
.inv-qty.is-blank { color: var(--muted); }
/* 剩余数量另给一个类：数量那一列的选择器还有测试和样式在用，不共用免得混 */
.inv-remain { width: 90px; font-variant-numeric: tabular-nums; }
.inv-remain.is-blank { color: var(--muted); }
.inv-disp { width: 108px; white-space: nowrap; }
.inv-disp.is-blank { color: var(--muted); }
.inv-disp select { width: 100%; min-height: 24px; border: 1px solid var(--line); border-radius: 3px; font: inherit; background: #fff; }
.inv-edit { cursor: text; }
.inv-edit:hover { background: rgba(0, 117, 174, .07); box-shadow: inset 0 0 0 1px rgba(0, 117, 174, .3); }
.inv-edit.is-editing { box-shadow: inset 0 0 0 2px var(--blue); }
.inv-wrap input { width: 100%; min-height: 24px; padding: 0 4px; border: 1px solid var(--blue); border-radius: 3px; font: inherit; }
.inv-note { margin: 6px 0 0; color: var(--muted); font-size: 11px; line-height: 1.7; }
</style>
